import { IonButton, IonItem, IonSearchbar, IonSelect, IonSelectOption } from '@ionic/react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useReceivedDocs } from '../../api/hooks/receivedDocs/useReceivedDocs';
import { DataPlaceholder } from '../../components/atoms/DataPlaceholder';
import { Loading } from '../../components/atoms/Loading';
import { SUBSCRIPTION } from '../../interfaces/enumerables';
import { AadeBookInvoiceType } from '../../interfaces/myData.interface';
import { invTypeI18nKey } from '../../interfaces/myDataInvTypes';
import useDebounce from '../../services/hooks/useDebounce';
import { useHasActivePlan } from '../../services/utils/useHasActivePlan';
import { images } from '../../theme/assets/img';
import { ReceivedDocItemCard } from './components/ReceivedDocItemCard';

const DATE_FORMAT = 'DD/MM/YYYY';

const INV_TYPE_OPTIONS = [
  '1.1',
  '1.2',
  '1.3',
  '1.4',
  '1.5',
  '1.6',
  '2.1',
  '2.2',
  '2.3',
  '2.4',
  '3.1',
  '3.2',
  '5.1',
  '5.2',
  '6.1',
  '6.2',
  '7.1',
  '8.1',
  '8.2',
  '8.4',
  '8.5',
  '9.1',
  '9.2',
  '9.3',
  '9.33',
  '10.1',
  '10.2',
  '11.1',
  '11.2',
  '11.3',
  '11.4',
  '11.5',
  '13.1',
  '13.2',
  '13.3',
  '13.4',
  '14.1',
  '14.2',
  '14.3',
  '14.4',
  '14.5',
  '16.1',
];

const defaultDateFrom = () => dayjs().startOf('month');
const defaultDateTo = () => dayjs();

export const ReceivedDocsScreen = () => {
  const { t } = useTranslation();
  useHasActivePlan(true, SUBSCRIPTION.SUBTOTALS);

  const [searchParams, setSearchParams] = useSearchParams();

  // Search input is kept as local state for smooth typing; the debounced value is what drives the query
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const debouncedSearch = useDebounce(search, 400);

  // Derive filter values from URL — fall back to defaults when params are absent
  const dateFrom = useMemo(() => {
    const p = searchParams.get('dateFrom');
    return p ? dayjs(p) : defaultDateFrom();
  }, [searchParams]);

  const dateTo = useMemo(() => {
    const p = searchParams.get('dateTo');
    return p ? dayjs(p) : defaultDateTo();
  }, [searchParams]);

  const invType = searchParams.get('invType') || '';

  // Sync debounced search to URL (replace so filter changes don't pollute back history)
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('search', debouncedSearch);
        else next.delete('search');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch]);

  const setDateFrom = useCallback(
    (d: Dayjs) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('dateFrom', d.format('YYYY-MM-DD'));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setDateTo = useCallback(
    (d: Dayjs) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('dateTo', d.format('YYYY-MM-DD'));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setInvType = useCallback(
    (val: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (val) next.set('invType', val);
          else next.delete('invType');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleReset = useCallback(() => {
    setSearch('');
    setSearchParams(
      {
        dateFrom: defaultDateFrom().format('YYYY-MM-DD'),
        dateTo: defaultDateTo().format('YYYY-MM-DD'),
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const filters = useMemo(
    () => ({
      dateFrom: dateFrom.format('YYYY-MM-DD'),
      dateTo: dateTo.format('YYYY-MM-DD'),
      invType,
      counterVatNumber: debouncedSearch.length === 9 ? debouncedSearch : '',
      markSearch: debouncedSearch.length !== 9 ? debouncedSearch : '',
    }),
    [dateFrom, dateTo, invType, debouncedSearch],
  );

  const { data, fetchNextPage, isFetching, isFetchingNextPage, isLoading } = useReceivedDocs(filters);

  const allDocs = useMemo<AadeBookInvoiceType[]>(
    () => (data?.pages ?? []).flatMap((p) => p.docs).sort((a, b) => Number(b.mark) - Number(a.mark)),
    [data?.pages],
  );

  const hasMore = (data?.pages[data.pages.length - 1]?.continuationToken ?? null) !== null;

  return (
    <div className="entity-pagination-wrapper">
      <div className="entity-pagination-filters pagination-layout">
        <div className="list-padding-horizontal">
          <div className="flex-space-between-center-row-wrap margin-t-16 margin-b-10 gap-8">
            <div className="flex-start-center-row-wrap gap-8">
              <IonItem
                className="border-radius-input g-select-border cursorPointer"
                mode="md"
                style={{ '--min-height': '44px', minWidth: 170 } as React.CSSProperties}>
                <DatePicker
                  className="normalText4 antd-datetime-container"
                  value={dateFrom}
                  maxDate={dateTo}
                  onChange={(d) => d && setDateFrom(d)}
                  format={DATE_FORMAT}
                  variant="borderless"
                  allowClear={false}
                  placeholder={t('RECEIVED_DOCS.DATE_FROM')}
                />
              </IonItem>
              <IonItem
                className="border-radius-input g-select-border cursorPointer"
                mode="md"
                style={{ '--min-height': '44px', minWidth: 170 } as React.CSSProperties}>
                <DatePicker
                  className="normalText4 antd-datetime-container"
                  value={dateTo}
                  minDate={dateFrom}
                  onChange={(d) => d && setDateTo(d)}
                  format={DATE_FORMAT}
                  variant="borderless"
                  allowClear={false}
                  placeholder={t('RECEIVED_DOCS.DATE_TO')}
                />
              </IonItem>

              <div
                className="g-select-border border-radius-input cursorPointer"
                style={{ minWidth: 100, height: 44, display: 'flex', alignItems: 'center' }}>
                <IonSelect
                  mode="ios"
                  aria-label="Doc type"
                  interface="action-sheet"
                  value={invType || null}
                  className="normalText4 cursorPointer select-outline"
                  style={{ width: '100%' }}
                  placeholder={t('RECEIVED_DOCS.INV_TYPE')}
                  cancelText={t('SHARED.CANCEL')}
                  onIonChange={(e) => setInvType(e.detail.value ?? '')}>
                  <IonSelectOption value="">—</IonSelectOption>
                  {INV_TYPE_OPTIONS.map((code) => (
                    <IonSelectOption key={code} value={code}>
                      {code} – {t(invTypeI18nKey(code), { defaultValue: code })}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </div>
              <IonButton
                mode="md"
                fill="outline"
                onClick={handleReset}
                className="ion-reset-button"
                style={{ height: 44 }}>
                {t('RECEIVED_DOCS.RESET')}
              </IonButton>
            </div>
            <IonSearchbar
              mode="md"
              className="g-search"
              value={search}
              placeholder={t('RECEIVED_DOCS.SEARCH')}
              style={{ width: 260 }}
              onIonInput={(e) => setSearch(e.detail.value ?? '')}
            />
          </div>
        </div>
      </div>

      {isLoading && <Loading />}

      {!isLoading && allDocs.length === 0 && (
        <DataPlaceholder imgSource={images.noData} msg={'RECEIVED_DOCS.NO_DATA'} />
      )}

      {allDocs.length > 0 && (
        <div className="entity-pagination-content pagination-layout">
          <div className="entity-pagination-list list-padding-horizontal list-items-itemOfList padding-t-10">
            {allDocs.map((doc, index) => (
              <ReceivedDocItemCard key={doc.mark} index={index} doc={doc} />
            ))}
          </div>

          {hasMore && (
            <div className="flex-center-center-row-wrap padding-t-16 padding-b-16">
              <IonButton
                mode="md"
                fill="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage || isFetching}>
                {isFetchingNextPage ? <Loading /> : t('RECEIVED_DOCS.LOAD_MORE')}
              </IonButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
